import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AuthService } from '@app/core/services';
import { RefreshTokenCoordinatorService } from '@app/core/services/refresh-token-coordinator.service';
import { ApiRequestError } from '@app/core/models/api-error.model';
import { getFullRelativeUrl } from '../utils/url.util';

const SESSION_ENDED_ERROR = new Error('The session has ended.');

const PUBLIC_AUTH_ENDPOINTS = [
  '/accounts/login',
  '/accounts/refresh-token',
  '/accounts/logout',
  '/accounts/available-otp-types',
];

export const refreshTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const coordinator = inject(RefreshTokenCoordinatorService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (
        error.status !== 401 ||
        PUBLIC_AUTH_ENDPOINTS.some(endpoint => req.url.includes(endpoint)) ||
        authService.isLoggingOut
      ) {
        return throwError(() => error);
      }

      return handleUnauthorized(req, next, authService, coordinator, error);
    }),
  );
};

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  coordinator: RefreshTokenCoordinatorService,
  originalError: unknown,
): Observable<HttpEvent<unknown>> {
  if (!authService.hasRefreshToken()) {
    authService.logout(getFullRelativeUrl());
    return throwError(() => originalError);
  }

  if (coordinator.isRefreshing) {
    return coordinator
      .waitForToken()
      .pipe(switchMap(newAccessToken => retryWithToken(req, next, newAccessToken)));
  }

  coordinator.start();

  return authService.refreshAccessToken().pipe(
    catchError(error => {
      coordinator.fail(error);

      if (!(error instanceof ApiRequestError && error.status === 403)) {
        authService.logout(getFullRelativeUrl());
      }

      return throwError(() => error);
    }),
    switchMap(newAccessToken => {
      if (authService.isLoggingOut) {
        coordinator.fail(SESSION_ENDED_ERROR);
        return throwError(() => SESSION_ENDED_ERROR);
      }

      if (!newAccessToken) {
        coordinator.fail(SESSION_ENDED_ERROR);
        authService.logout(getFullRelativeUrl());
        return throwError(() => SESSION_ENDED_ERROR);
      }

      coordinator.publish(newAccessToken);
      return retryWithToken(req, next, newAccessToken);
    }),
    finalize(() => coordinator.finish()),
  );
}

function retryWithToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  accessToken: string,
): Observable<HttpEvent<unknown>> {
  return next(
    req.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    }),
  );
}
